import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';

import DetailViewButton, { IconPosition } from '../../../elements/DetailViewButton';
import Image from '../../Image/Image';
import Resizeable from '../../Resizeable/Resizeable';
import AdvanceModeButton from '../../../elements/AdvanceModeButton';

import { useNavigationContext } from '../../../context/NavigationContext';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import { useAudioContext } from '../../../context/AudioContext';
import { DetailSectionIDs, SectionIDs } from '../../../data/Sections';
import FocusPoints from '../../../data/FocusPoints';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/images/icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../../assets/images/icons/arrow-right.svg';

import styles from './styles.module.scss';
import useIsMobile from '../../../hooks/useIsMobile';

interface IDetail {
  id: DetailSectionIDs,
  imageName: string,
  subtitle: string,
  title: string,
  title2: string,
  text: string
}

export default function DetailSidePanel(): JSX.Element {
  const isMobile = useIsMobile();
  const { playAudio } = useAudioContext();
  const { t } = useTranslation('ai4Jokes');
  const { moveToTheSection } = useNavigationContext();
  const { activeDetailID, setDetailSectionID, setActiveFocusID, advancedModeOn } = useDetailViewContext();

  const scrollContainerRef = useRef<HTMLDivElement>();

  const details = useMemo<IDetail[]>(() => [
    {
      id: DetailSectionIDs.DecodingVector,
      imageName: 'Vector-representation',
      subtitle: t('vectorrepresentation.subtitle'),
      title: t('vectorrepresentation.title_1'),
      title2: t('vectorrepresentation.title_2'),
      text: t('vectorrepresentation.paragraph')
    },
    {
      id: DetailSectionIDs.DecodingSphere,
      imageName: 'SphereEmbedding',
      subtitle: t('sphereembedding.subtitle'),
      title: t('sphereembedding.title_1'),
      title2: t('sphereembedding.title_2'),
      text: t('sphereembedding.paragraph')
    },
    {
      id: DetailSectionIDs.DecodingSubspaces,
      imageName: 'SphereSubspaces',
      subtitle: t('spheresubspaces.subtitle'),
      title: t('spheresubspaces.title_1'),
      title2: t('spheresubspaces.title_2'),
      text: t('spheresubspaces.paragraph')
    },
    {
      id: DetailSectionIDs.DecodingTopology,
      imageName: 'TopologicalRelations',
      subtitle: t('topologicalrelations.subtitle'),
      title: t('topologicalrelations.title_1'),
      title2: t('topologicalrelations.title_2'),
      text: t('topologicalrelations.paragraph')
    },
    {
      id: DetailSectionIDs.DecodingDynamicBehaviour,
      imageName: 'Dynamicbehaviour',
      subtitle: t('dynamicbehavior.subtitle'),
      title: t('dynamicbehavior.title_1'),
      title2: t('dynamicbehavior.title_2'),
      text: t('dynamicbehavior.paragraph')
    }
  ], [t]);

  const selectedDetail = useMemo(() =>
    details.find(d => d.id === activeDetailID),
  [activeDetailID, details]);
  const currentIndex = useMemo(() =>
    details.findIndex(d => d.id === selectedDetail?.id),
  [selectedDetail, details]);

  const nextDetailThumbnail = useMemo(() => {
    if (!selectedDetail)
      return '';
    const nextIndex = (currentIndex + 1) % details.length;
    return details[nextIndex].imageName;
  }, [selectedDetail, currentIndex, details]);

  const prevDetailThumbnail = useMemo(() => {
    if (!selectedDetail)
      return '';
    const nextIndex = (details.length + currentIndex - 1) % details.length;
    return details[nextIndex].imageName;
  }, [selectedDetail, currentIndex, details]);

  const onNextClick = useCallback(
    () => {
      playAudio('audioNextPrev');
      let index = details.findIndex(d => d.id === activeDetailID);
      index = (index + 1) % details.length;
      setDetailSectionID(details[index].id);
    },
    [activeDetailID, setDetailSectionID, playAudio, details],
  );
  const onPrevClick = useCallback(
    () => {
      playAudio('audioNextPrev');
      let index = details.findIndex(d => d.id === activeDetailID);
      index = (details.length + index - 1) % details.length;
      setDetailSectionID(details[index].id);
    },
    [activeDetailID, setDetailSectionID, playAudio, details],
  );

  const onAdvancedModeClick = useCallback(
    () => {
      playAudio('audioAdvancedMode');
      setActiveFocusID(FocusPoints.Overview);
    },
    [setActiveFocusID, playAudio],
  );

  const onToTimelineClick = useCallback(
    () => {
      playAudio('audioCTA');
      setDetailSectionID(DetailSectionIDs.None);
    },
    [setDetailSectionID, playAudio],
  );

  const toConclusion = useCallback(
    () => {
      playAudio('audioCTA');
      setDetailSectionID(DetailSectionIDs.None);
      moveToTheSection(SectionIDs.Conclusion);
    },
    [setDetailSectionID, moveToTheSection, playAudio],
  );

  const showAdvanceModeButton = useMemo(() =>
    activeDetailID === DetailSectionIDs.DecodingSubspaces ||
    activeDetailID === DetailSectionIDs.DecodingTopology ||
    activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour,
  [activeDetailID]);

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    // Scroll to top again when switching sections
    scrollContainerRef.current.scrollTo(0, 0);
  }, [selectedDetail]);

  return (
    <>
      {selectedDetail &&
        <>
          <Resizeable>
            {
              showAdvanceModeButton &&
              <AdvanceModeButton click={onAdvancedModeClick}></AdvanceModeButton>
            }
            {/* @ts-expect-error ref */}
            <div className={styles.whiteModeSidePanel} ref={scrollContainerRef} data-scrolly={!advancedModeOn}>
              <div className={styles.innerContainer}>
                <div className={styles.panelHeader}>
                  <Image
                    alt='snapshot from the video'
                    folder='thumbnails'
                    filename={selectedDetail.imageName}
                  />
                  <span>{parse(selectedDetail.subtitle)}</span>
                </div>
                <div className={styles.content}>
                  <div className={styles.title}>{selectedDetail.title}</div>
                  <div className={styles.title2}>{selectedDetail.title2}</div>
                  <div className={[styles.text, styles.contentCustomScroll].join(' ')}>
                    {parse(selectedDetail.text)}
                  </div>
                </div>
              </div>
            </div>
            {!isMobile &&
              <button
                className={styles.toTimelineButton}
                onClick={onToTimelineClick}>
                <ArrowLeftIcon />
                {t('thescience.buttons.timeline')}
              </button>
            }
          </Resizeable>

          <div className={styles.buttonContainer}>
            <DetailViewButton
              data-visibility={currentIndex !== 0}
              click={onPrevClick}
              title={t('thescience.buttons.prev')}
              icon={<ArrowLeftIcon />}
              thumbnail={prevDetailThumbnail}
              iconPosition={IconPosition.Left} />
            {
              currentIndex !== 4 &&
              <DetailViewButton
                data-visibility={currentIndex !== 4}
                click={onNextClick}
                title={t('thescience.buttons.next')}
                icon={<ArrowRightIcon />}
                thumbnail={nextDetailThumbnail}
                iconPosition={IconPosition.Right} />
            }

            {currentIndex === 4 &&
              <button
                className={styles.toConclusionButton}
                onClick={toConclusion}
              >
                {t('thescience.buttons.conclusion')}
              </button>
            }
          </div>
        </>
      }
    </>
  );
}
